import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  X,
  Bookmark,
  Download,
  Printer,
  Search,
  StickyNote,
  Lock,
  Crown,
  Award,
  Linkedin,
  Headphones,
  HelpCircle,
  Calculator,
} from "lucide-react";
import { chapters } from "../../data/chapters";
import { Progress } from "../ui/progress";
import { ScrollArea } from "../ui/scroll-area";
import { generateCertificate } from "../../utils/generateCertificate";
import RatingWidget from "./RatingWidget";

function chapterRowClass({ locked, isActive }) {
  if (locked) return "opacity-50 cursor-not-allowed";
  if (isActive) return "border-2 border-[var(--c-border)] bg-[#1E3A8A]/10 shadow-[2px_2px_0px_var(--c-shadow)]";
  return "hover:bg-[var(--c-hover-bg)]";
}

function chapterBadgeClass({ locked, allDone, isActive }) {
  if (locked) return "border-[var(--c-border-light)] bg-[var(--c-surface)] text-[var(--c-muted)]";
  if (allDone) return "border-[var(--c-border)] bg-[#A3E6D5] text-[#1A1918]";
  if (isActive) return "border-[var(--c-border)] bg-[#1E3A8A] text-white";
  return "border-[var(--c-border-light)] bg-[var(--c-surface)] text-[var(--c-muted)]";
}

function ChapterBadgeContent({ locked, premiumLocked, allDone, chapter }) {
  if (locked) return premiumLocked ? <Crown className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />;
  if (allDone) return <CheckCircle2 className="h-4 w-4" />;
  return chapter.isPrerequisite ? "0" : chapter.id;
}

function chapterStatusText({ premiumLocked, locked, chCompleted, chTotal }) {
  if (premiumLocked) return "Premium";
  if (locked) return "Locked";
  return `${chCompleted}/${chTotal} tasks`;
}

export default function TocSidebar({
  sidebarOpen,
  user,
  progressPercent,
  completedTotal,
  totalChecklist,
  searchQuery,
  setSearchQuery,
  filteredChapters,
  chapterChecklistStart,
  completedSteps,
  bookmarkedChapters,
  notes,
  currentPage,
  isCostPage,
  isPageUnlocked,
  requiresPremium,
  goToPage,
  handleExportPdf,
  allComplete,
}) {
  const navigate = useNavigate();

  const handleCertificate = () => {
    const date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    generateCertificate(user?.name || user?.email || "Valued Customer", date);
    toast.success("Certificate downloaded!", { duration: 2000 });
  };

  return (
    <aside
      data-testid="toc-sidebar"
      className={`fixed inset-y-0 left-0 z-30 w-80 transform border-r-2 border-[var(--c-border)] bg-[var(--c-surface)] transition-transform lg:static lg:block lg:w-72 lg:translate-x-0 lg:rounded-xl lg:border-2 lg:shadow-[4px_4px_0px_var(--c-shadow)] ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex h-full flex-col">
        <div className="border-b-2 border-[var(--c-border)] p-5">
          <h2 className="font-heading text-lg font-bold text-[var(--c-text)]">Table of Contents</h2>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-[var(--c-muted)]">
              <span>{progressPercent}% complete</span>
              <span>{completedTotal}/{totalChecklist} tasks</span>
            </div>
            <Progress
              data-testid="overall-progress"
              value={progressPercent}
              className="mt-2 h-2.5 border border-[var(--c-border)] bg-[var(--c-input-bg)]"
            />
          </div>
          {/* Search */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--c-muted)]" />
            <input
              data-testid="chapter-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search chapters..."
              className="w-full rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] py-2 pl-9 pr-3 text-xs font-medium text-[var(--c-text)] placeholder:text-[var(--c-muted)] focus:border-[#1E3A8A] focus:outline-none focus:ring-1 focus:ring-[#1E3A8A]/20"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--c-muted)] hover:text-[var(--c-text)]"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Action buttons */}
          <div className="mt-3 flex gap-2">
            <button
              data-testid="export-pdf-button"
              onClick={handleExportPdf}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A]/5 px-2 py-2 text-xs font-bold text-[#1E3A8A] transition-all hover:-translate-y-0.5 hover:bg-[#1E3A8A]/10 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
            >
              <Download className="h-3.5 w-3.5" />
              PDF
            </button>
            <button
              data-testid="print-checklist-button"
              onClick={() => navigate("/checklist")}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A]/5 px-2 py-2 text-xs font-bold text-[#1E3A8A] transition-all hover:-translate-y-0.5 hover:bg-[#1E3A8A]/10 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
            >
              <Printer className="h-3.5 w-3.5" />
              Print
            </button>
          </div>
          {/* Certificate button - only when all complete */}
          {allComplete && (
            <div className="mt-2 space-y-1.5">
              <button
                data-testid="sidebar-certificate-button"
                onClick={handleCertificate}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-[#A3E6D5] bg-[#A3E6D5]/15 px-2 py-2 text-xs font-bold text-[var(--c-text)] transition-all hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
              >
                <Award className="h-3.5 w-3.5 text-[#1E3A8A]" />
                Certificate
              </button>
              <a
                data-testid="sidebar-linkedin-button"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-[#0A66C2]/40 bg-[#0A66C2]/5 px-2 py-2 text-xs font-bold text-[#0A66C2] transition-all hover:-translate-y-0.5 hover:bg-[#0A66C2]/10 hover:shadow-[2px_2px_0px_var(--c-shadow)]"
              >
                <Linkedin className="h-3.5 w-3.5" />
                Share on LinkedIn
              </a>
              <RatingWidget compact />
            </div>
          )}
        </div>
        {/* Need Help CTA */}
        <div className="border-t-2 border-[var(--c-border)] p-4 space-y-2">
          <button
            data-testid="need-help-cta"
            onClick={() => navigate("/services")}
            className="flex w-full items-center gap-3 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_var(--c-shadow)]"
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A]">
              <Headphones className="h-4 w-4 text-white" />
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--c-text)]">Need help?</p>
              <p className="text-[10px] text-[var(--c-muted)]">We&apos;ll file your LLC for you</p>
            </div>
          </button>
          <button
            data-testid="faq-link"
            onClick={() => navigate("/faq")}
            className="flex w-full items-center gap-3 rounded-xl border-2 border-[var(--c-border-light)] bg-[var(--c-bg)] p-3 text-left transition-all hover:-translate-y-0.5 hover:border-[var(--c-border)] hover:shadow-[3px_3px_0px_var(--c-shadow)]"
          >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border-2 border-[var(--c-border-light)] bg-[var(--c-surface)]">
              <HelpCircle className="h-4 w-4 text-[#1E3A8A]" />
            </div>
            <div>
              <p className="text-xs font-bold text-[var(--c-text)]">FAQ</p>
              <p className="text-[10px] text-[var(--c-muted)]">Common LLC questions</p>
            </div>
          </button>
        </div>
        <ScrollArea className="flex-1 p-3">
          <div className="space-y-1">
            {/* Search results indicator */}
            {searchQuery.trim() && (
              <p className="px-3 py-1 text-xs font-bold text-[var(--c-muted)]">
                {filteredChapters.length} result{filteredChapters.length !== 1 ? "s" : ""}
              </p>
            )}
            {filteredChapters.map((ch) => {
              const i = chapters.indexOf(ch);
              const startIdx = chapterChecklistStart[i];
              const chCompleted = ch.checklistItems.filter(
                (_, j) => completedSteps.includes(startIdx + j)
              ).length;
              const chTotal = ch.checklistItems.length;
              const isActive = currentPage === i;
              const allDone = chCompleted === chTotal;
              const isBookmarked = bookmarkedChapters.includes(ch.id);
              const hasNote = notes[String(ch.id)]?.trim();
              const locked = !isPageUnlocked(i);
              const premiumLocked = requiresPremium(i);

              return (
                <button
                  key={ch.id}
                  data-testid={`toc-chapter-${ch.id}`}
                  onClick={() => goToPage(i)}
                  className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-all ${chapterRowClass({ locked, isActive })}`}
                >
                  <div
                    className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border-2 text-xs font-bold ${chapterBadgeClass({ locked, allDone, isActive })}`}
                  >
                    <ChapterBadgeContent locked={locked} premiumLocked={premiumLocked} allDone={allDone} chapter={ch} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <p
                        className={`text-sm font-semibold leading-tight ${
                          isActive ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"
                        }`}
                      >
                        {ch.title}
                      </p>
                      {isBookmarked && (
                        <Bookmark className="h-3.5 w-3.5 flex-shrink-0 fill-[#1E3A8A] text-[#1E3A8A]" />
                      )}
                      {hasNote && (
                        <StickyNote className="h-3 w-3 flex-shrink-0 text-[#A3E6D5]" />
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-[var(--c-muted-60)]">
                      {chapterStatusText({ premiumLocked, locked, chCompleted, chTotal })}
                    </p>
                  </div>
                </button>
              );
            })}
            {/* Cost Estimator entry */}
            {(!searchQuery.trim() || "cost estimator calculator startup".includes(searchQuery.toLowerCase())) && (
              <button
                data-testid="toc-cost-estimator"
                onClick={() => goToPage(chapters.length)}
                className={`flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-all ${
                  isCostPage
                    ? "border-2 border-[var(--c-border)] bg-[#A3E6D5]/20 shadow-[2px_2px_0px_var(--c-shadow)]"
                    : "hover:bg-[var(--c-hover-bg)]"
                }`}
              >
                <div
                  className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border-2 text-xs font-bold ${
                    isCostPage
                      ? "border-[var(--c-border)] bg-[#A3E6D5] text-[#1A1918]"
                      : "border-[var(--c-border-light)] bg-[var(--c-surface)] text-[var(--c-muted)]"
                  }`}
                >
                  <Calculator className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm font-semibold leading-tight ${
                      isCostPage ? "text-[var(--c-text)]" : "text-[var(--c-muted)]"
                    }`}
                  >
                    Cost Estimator
                  </p>
                  <p className="mt-0.5 text-xs text-[var(--c-muted-60)]">
                    Calculate your startup costs
                  </p>
                </div>
              </button>
            )}
          </div>
        </ScrollArea>
      </div>
    </aside>
  );
}
